import * as XLSX from 'xlsx'
import moment from 'moment'
import { getPatientsLoaded } from '@/components/patients/patients.service'
import { PatientsLoadedResolve } from '@/models'

const DOWNLOADED_KEY = 'patientUploadResultsDownloaded'

export function clearPatientUploadResultsDownload() {
  sessionStorage.removeItem(DOWNLOADED_KEY)
}

async function fetchAllLoadedPatients(
  uploadId: string
): Promise<PatientsLoadedResolve[]> {
  const pageSize = 100
  let page = 1
  let totalPages = 1
  const all: PatientsLoadedResolve[] = []

  do {
    const response = await getPatientsLoaded(page, pageSize, uploadId)
    const chunk = response?.data || []
    all.push(...chunk)

    const raw = response?.headers?.['x-pagination']
    if (raw) {
      try {
        const pagination = JSON.parse(raw)
        totalPages = pagination.totalPages || pagination.TotalPages || 1
      } catch {
        totalPages = 1
      }
    } else if (!chunk.length) {
      totalPages = page
    }

    page += 1
  } while (page <= totalPages)

  return all
}

function buildRows(data: PatientsLoadedResolve[]) {
  return data.map(row => ({
    'Upload Identifier': row.uploadIdentifier,
    NPI: row.npi,
    'First Name': row.firstName,
    'Last Name': row.lastName,
    Sex: row.sex,
    DOB: row.dob ? moment(row.dob).format('MM/DD/YYYY') : '',
    'Insurance Name': row.insuranceName,
    'Insurance Plan': row.insurancePlan,
    Phone: row.phone,
    Mobile: row.mobile,
    Email: row.email1,
    Street: row.street,
    City: row.city,
    State: row.state,
    Zip: row.zip,
    'Insurance Contract No': row.insuranceContractNo,
    'Upload Status': row.uploadStatus,
    'Upload Message Status': row.uploadMessageStatus,
    'Upload Date': row.uploadDate
      ? moment(row.uploadDate).format('MM/DD/YYYY')
      : ''
  }))
}

/** Downloads an .xlsx with all staged results for the batch. Idempotent per uploadId. */
export async function downloadPatientUploadResults(
  uploadId: string
): Promise<boolean> {
  if (!uploadId) return false
  if (sessionStorage.getItem(DOWNLOADED_KEY) === uploadId) return false
  sessionStorage.setItem(DOWNLOADED_KEY, uploadId)

  const allData = await fetchAllLoadedPatients(uploadId)
  if (allData.length === 0) {
    sessionStorage.removeItem(DOWNLOADED_KEY)
    return false
  }

  const sheet = XLSX.utils.json_to_sheet(buildRows(allData))
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, 'Results')
  XLSX.writeFile(
    workbook,
    `patient-upload-results-${moment().format('YYYY-MM-DD-HHmmss')}.xlsx`
  )
  return true
}
